import showdown from 'showdown';
import TurndownService from 'turndown';

/**
 * Conversor de Markdown para HTML e vice-versa
 * Mantém compatibilidade entre o armazenamento em Markdown e o editor WYSIWYG
 */

// Configuração do Showdown (Markdown → HTML)
const showdownConverter = new showdown.Converter({
  headerLevelStart: 1,
  simplifiedAutoLink: true,
  strikethrough: true,
  tables: true,
  tasklists: true,
  simpleLineBreaks: true,
  openLinksInNewWindow: false,
  emoji: false,
});

// Configuração do Turndown (HTML → Markdown)
const turndownService = new TurndownService({
  headingStyle: 'atx', // Usa # para títulos
  hr: '---',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
  emDelimiter: '*',
  strongDelimiter: '**',
});

// Markdown não tem sintaxe nativa para alinhamento de parágrafos.
// Preservamos via HTML embutido — Showdown passa HTML adiante na conversão de volta,
// e o gerador de DOCX (LLM markdown→XML) usa como hint de formatação.
turndownService.addRule('preserveQuillAlignment', {
  filter: (node) => {
    if (!node || node.nodeType !== 1 || node.nodeName !== 'P') return false;
    const cls = node.className || '';
    return /ql-align-(center|right|justify)/.test(cls);
  },
  replacement: (_content, node) => {
    const cls = node.className || '';
    let align = '';
    if (/ql-align-justify/.test(cls)) align = 'justify';
    else if (/ql-align-center/.test(cls)) align = 'center';
    else if (/ql-align-right/.test(cls)) align = 'right';
    if (!align) return `\n\n${_content}\n\n`;
    // innerHTML preserva formatação (bold, italic, links) dentro do parágrafo alinhado.
    // Classe ql-align-X faz o Quill reconhecer no round-trip; style serve como hint para o LLM.
    const inner = (node.innerHTML || '').trim();
    return `\n\n<p class="ql-align-${align}" style="text-align: ${align};">${inner}</p>\n\n`;
  },
});

/**
 * Converte Markdown para HTML
 * @param {string} markdown - Texto em formato Markdown
 * @returns {string} HTML gerado a partir do Markdown
 */
export const markdownToHtml = (markdown) => {
  if (!markdown || markdown.trim() === '') {
    return '';
  }
  return showdownConverter.makeHtml(markdown);
};

/**
 * Converte HTML para Markdown
 * @param {string} html - Texto em formato HTML
 * @returns {string} Markdown gerado a partir do HTML
 */
export const htmlToMarkdown = (html) => {
  if (!html || html.trim() === '' || html === '<p><br></p>') {
    return '';
  }
  
  // Remove tags vazias do Quill
  let cleanHtml = html.replace(/<p><br><\/p>/g, '\n');
  cleanHtml = cleanHtml.replace(/<p>\s*<\/p>/g, '');
  
  const markdown = turndownService.turndown(cleanHtml);
  
  // Limpa espaços extras
  return markdown.trim();
};

/**
 * Verifica se o conteúdo parece ser HTML
 * @param {string} content - Conteúdo a ser verificado
 * @returns {boolean} true se parecer HTML, false se parecer Markdown
 */
export const isHtml = (content) => {
  if (!content) return false;
  
  // Verifica se tem tags HTML comuns
  const htmlPattern = /<\/?[a-z][\s\S]*>/i;
  return htmlPattern.test(content);
};

/**
 * Normaliza o conteúdo para garantir que está em HTML para o editor
 * Se receber Markdown, converte para HTML
 * @param {string} content - Conteúdo em Markdown ou HTML
 * @returns {string} Conteúdo em HTML
 */
export const normalizeToHtml = (content) => {
  if (!content || content.trim() === '') {
    return '';
  }
  
  // Se já for HTML, retorna como está
  if (isHtml(content)) {
    return content;
  }
  
  // Se for Markdown, converte para HTML
  return markdownToHtml(content);
};

/**
 * Normaliza o conteúdo para garantir que está em Markdown para armazenamento
 * Se receber HTML, converte para Markdown
 * @param {string} content - Conteúdo em HTML ou Markdown
 * @returns {string} Conteúdo em Markdown
 */
export const normalizeToMarkdown = (content) => {
  if (!content || content.trim() === '' || content === '<p><br></p>') {
    return '';
  }
  
  // Se já for Markdown (não tem tags HTML), retorna como está
  if (!isHtml(content)) {
    return content;
  }
  
  // Se for HTML, converte para Markdown
  return htmlToMarkdown(content);
};


